import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { showNotification } from './Notification';
import './ProfileUpdateLink.css';

const initialFormState = {
  serial_number: '',
  tag_number: '',
  first_name: '',
  last_name: '',
  sex: '',
  date_of_birth: '',
  pwd: '',
  phone_number: '',
  national_id_number: '',
  bank_verification_number: '',
  account_number: '',
  bank_name: '',
  email: '',
  state_of_residence: '',
  lga_of_residence: '',
  parent_guardian_name: '',
  parent_guardian_address: '',
  parent_guardian_phone: '',
  education: '',
  employment_status: '',
  completion_status: '',
  specialization: '',
  status: 'active',
  batch_id: '',
};

const ProfileUpdateLink = () => {
  const { token } = useParams();
  const { theme } = useContext(ThemeContext);
  const [linkInfo, setLinkInfo] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selection, setSelection] = useState({ batch_id: '', tag_number: '' });
  const [trainee, setTrainee] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const response = await axios.get(`/api/profile-links/link/${token}`);
        setLinkInfo(response.data.link);
        setBatches(response.data.batches || []);
      } catch {
        showNotification('Invalid or expired update link', 'error');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  const handleFetchTrainee = async (e) => {
    e.preventDefault();
    setFetching(true);
    setTrainee(null);
    setFormData(initialFormState);
    try {
      const response = await axios.post(`/api/profile-links/link/${token}/fetch`, selection);
      const traineeData = response.data.trainee;
      setTrainee(traineeData);
      setFormData({
        ...initialFormState,
        ...traineeData,
        batch_id: traineeData.batch_id || '',
        date_of_birth: traineeData.date_of_birth ? traineeData.date_of_birth.split('T')[0] : '',
      });
      showNotification('Trainee details loaded. You can now make updates.', 'success');
    } catch (error) {
      showNotification(error.response?.data?.error || 'Unable to find trainee', 'error');
    } finally {
      setFetching(false);
    }
  };

  const isFieldEditable = (field) => {
    if (!linkInfo || !linkInfo.editable_fields) return true; // If no restrictions, all fields editable
    if (linkInfo.editable_fields.length === 0) return true; // Empty array means all fields editable
    return linkInfo.editable_fields.includes(field);
  };

  const handleInputChange = (field, value) => {
    if (!isFieldEditable(field)) return; // Prevent changes to non-editable fields
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trainee) return;
    setSaving(true);
    try {
      await axios.put(`/api/profile-links/link/${token}/update`, {
        trainee_id: trainee.id,
        updates: formData,
      });
      showNotification('Details saved successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.error || 'Unable to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="profile-update-loading">Loading...</div>;
  }

  if (!linkInfo) {
    return <div className="profile-update-error">Invalid or expired link.</div>;
  }

  return (
    <div className="profile-update-link" data-theme={theme}>
      <div className="profile-update-card">
        <div className="profile-update-header">
          <img
            src="/logo/css-farms-tvet-logo.png"
            alt="CSS FARMS IDEAS-TVET"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1>Profile Update Portal</h1>
          <p>Review your records and keep them up-to-date.</p>
        </div>

        <form className="fetch-form" onSubmit={handleFetchTrainee}>
          <div className="form-row">
            <div className="form-group">
              <label>Select Batch *</label>
              <select
                value={selection.batch_id}
                onChange={(e) => setSelection({ ...selection, batch_id: e.target.value })}
                required
              >
                <option value="">Choose a batch</option>
                {Array.isArray(batches) && batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Tag Number (SN) *</label>
              <input
                type="text"
                value={selection.tag_number}
                onChange={(e) => setSelection({ ...selection, tag_number: e.target.value })}
                placeholder="Enter your SN"
                required
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={fetching}>
            {fetching ? 'Searching...' : 'Fetch My Details'}
          </button>
        </form>

        {trainee && (
          <form className="profile-update-form" onSubmit={handleSubmit}>
            <h2>Edit Your Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Batch</label>
                <select
                  value={formData.batch_id || ''}
                  onChange={(e) => handleInputChange('batch_id', e.target.value)}
                  disabled={!isFieldEditable('batch_id')}
                >
                  <option value="">No Batch</option>
                  {Array.isArray(batches) && batches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>SN (Serial Number)</label>
                  <input
                    type="text"
                    value={formData.serial_number}
                    onChange={(e) => handleInputChange('serial_number', e.target.value)}
                    disabled={!isFieldEditable('serial_number')}
                  />
              </div>
              <div className="form-group">
                <label>Tag Number</label>
                  <input
                    type="text"
                    value={formData.tag_number}
                    onChange={(e) => handleInputChange('tag_number', e.target.value)}
                    disabled={!isFieldEditable('tag_number')}
                  />
              </div>
              <div className="form-group">
                <label>First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    disabled={!isFieldEditable('first_name')}
                  />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    disabled={!isFieldEditable('last_name')}
                  />
              </div>
              <div className="form-group">
                <label>Sex</label>
                <select
                  value={formData.sex || ''}
                  onChange={(e) => handleInputChange('sex', e.target.value)}
                  disabled={!isFieldEditable('sex')}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                  <input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                    disabled={!isFieldEditable('date_of_birth')}
                  />
              </div>
              <div className="form-group">
                <label>PWD</label>
                <select
                  value={formData.pwd || ''}
                  onChange={(e) => handleInputChange('pwd', e.target.value)}
                  disabled={!isFieldEditable('pwd')}
                >
                  <option value="">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone_number || ''}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    disabled={!isFieldEditable('phone_number')}
                  />
              </div>
              <div className="form-group">
                <label>NIN</label>
                  <input
                    type="text"
                    value={formData.national_id_number || ''}
                    onChange={(e) => handleInputChange('national_id_number', e.target.value)}
                    disabled={!isFieldEditable('national_id_number')}
                  />
              </div>
              <div className="form-group">
                <label>BVN</label>
                  <input
                    type="text"
                    value={formData.bank_verification_number || ''}
                    onChange={(e) => handleInputChange('bank_verification_number', e.target.value)}
                    disabled={!isFieldEditable('bank_verification_number')}
                  />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                  <input
                    type="text"
                    value={formData.account_number || ''}
                    onChange={(e) => handleInputChange('account_number', e.target.value)}
                    disabled={!isFieldEditable('account_number')}
                  />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                  <input
                    type="text"
                    value={formData.bank_name || ''}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    disabled={!isFieldEditable('bank_name')}
                  />
              </div>
              <div className="form-group">
                <label>Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isFieldEditable('email')}
                  />
              </div>
              <div className="form-group">
                <label>State of Residence</label>
                  <input
                    type="text"
                    value={formData.state_of_residence || ''}
                    onChange={(e) => handleInputChange('state_of_residence', e.target.value)}
                    disabled={!isFieldEditable('state_of_residence')}
                  />
              </div>
              <div className="form-group">
                <label>LGA of Residence</label>
                  <input
                    type="text"
                    value={formData.lga_of_residence || ''}
                    onChange={(e) => handleInputChange('lga_of_residence', e.target.value)}
                    disabled={!isFieldEditable('lga_of_residence')}
                  />
              </div>
              <div className="form-group">
                <label>Parent/Guardian Name</label>
                  <input
                    type="text"
                    value={formData.parent_guardian_name || ''}
                    onChange={(e) => handleInputChange('parent_guardian_name', e.target.value)}
                    disabled={!isFieldEditable('parent_guardian_name')}
                  />
              </div>
              <div className="form-group full-width">
                <label>Parent/Guardian Address</label>
                <textarea
                  rows="3"
                  value={formData.parent_guardian_address || ''}
                  onChange={(e) => handleInputChange('parent_guardian_address', e.target.value)}
                  disabled={!isFieldEditable('parent_guardian_address')}
                />
              </div>
              <div className="form-group">
                <label>Parent/Guardian Phone</label>
                  <input
                    type="tel"
                    value={formData.parent_guardian_phone || ''}
                    onChange={(e) => handleInputChange('parent_guardian_phone', e.target.value)}
                    disabled={!isFieldEditable('parent_guardian_phone')}
                  />
              </div>
              <div className="form-group">
                <label>Education</label>
                  <input
                    type="text"
                    value={formData.education || ''}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    disabled={!isFieldEditable('education')}
                  />
              </div>
              <div className="form-group">
                <label>Employment Status</label>
                  <input
                    type="text"
                    value={formData.employment_status || ''}
                    onChange={(e) => handleInputChange('employment_status', e.target.value)}
                    disabled={!isFieldEditable('employment_status')}
                  />
              </div>
              <div className="form-group">
                <label>Completion Status</label>
                  <input
                    type="text"
                    value={formData.completion_status || ''}
                    onChange={(e) => handleInputChange('completion_status', e.target.value)}
                    disabled={!isFieldEditable('completion_status')}
                  />
              </div>
              <div className="form-group">
                <label>Specialization</label>
                  <input
                    type="text"
                    value={formData.specialization || ''}
                    onChange={(e) => handleInputChange('specialization', e.target.value)}
                    disabled={!isFieldEditable('specialization')}
                  />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status || 'active'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  disabled={!isFieldEditable('status')}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfileUpdateLink;

